import styled, { css } from 'styled-components';

import { getColor, getZIndex, getSpace } from '@yoweb/ui/styles/utils/theme';
import { OffsetParent } from '@yoweb/ui/components/containers';
import { FormMessage, Caption, Label, Required } from '@yoweb/ui/components/form/formStyles';
import { ErrorIcon } from '@yoweb/ui/components/Icon/ErrorIcon';
import { CheckIcon } from '@yoweb/ui/components/Icon/CheckIcon';
import { Interactive, Text } from '@yoweb/ui/components/typography';
import { isElevatedLabel } from './formUtils';
import type { FormInputLayoutProps } from '@yoweb/types/form';
import { Flex } from '@yoweb/ui/components/Flex';

export const Layout = ({
  // TODO: id should be renamed to htmlFor
  id,
  label,
  icon,
  caption,
  suffixLabel,
  error,
  success,
  placeholder,
  isFocused,
  hasValue,
  disabled = false,
  isRequired = false,
  textVariant,
  hideErrorMessage,
  children,
}: FormInputLayoutProps) => {
  const showBottomLabel = Boolean(error || caption || success || suffixLabel);
  const showSuccess = !error && success;

  const elevatedLabel = isElevatedLabel({
    placeholder,
    label,
    hasFocus: !!isFocused,
    hasValue,
  });

  return (
    <OffsetParent>
      <OffsetParent>
        <Label
          as="label"
          size={elevatedLabel ? 'sm' : 'lg'}
          htmlFor={id}
          isDisabled={disabled}
          elevatedLabel={elevatedLabel}
          data-testid="form-input-label"
          textVariant={textVariant}
        >
          {label}
          {/* eslint-disable-next-line react/jsx-no-literals */}
          {isRequired && <Required>{'*'}</Required>}
        </Label>
        {children}
        {icon && <IconWrap isDisabled={disabled}>{icon}</IconWrap>}
      </OffsetParent>
      {showBottomLabel && (
        <Caption>
          {error && !hideErrorMessage && (
            <FormMessage data-testid="input-error">
              <ErrorIcon size={16} variant="danger" />
              <Flex flexDirection="row" justifyContent="space-between">
                <Text as="div" size="sm" variant="danger">
                  {error}
                </Text>
                <Text
                  as="span"
                  size="sm"
                  textAlign="right"
                  variant="danger"
                  data-testid="textarea-suffix-label"
                >
                  {suffixLabel}
                </Text>
              </Flex>
            </FormMessage>
          )}
          {!error && suffixLabel && (
            <Text as="div" size="sm" textAlign="right" data-testid="textarea-suffix-label">
              {suffixLabel}
            </Text>
          )}
          {caption && (
            <Interactive size="sm" variant="muted" data-testid="form-caption-text">
              {caption}
            </Interactive>
          )}
          {showSuccess && (
            <FormMessage data-testid="form-caption-success">
              <CheckIcon size={16} variant="success" />
              {success}
            </FormMessage>
          )}
        </Caption>
      )}
    </OffsetParent>
  );
};

const IconWrap = styled.span<{ isDisabled: boolean }>`
  color: ${getColor('base700')};
  position: absolute;
  right: ${getSpace('normal1')};
  top: 50%;
  transform: translateY(-50%);
  z-index: ${getZIndex('layer')};

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: ${getColor('baseA300')};
    `};
`;

export default Layout;
